/**
 * `useShipPositionStream` — subscribes to `/sse/ships/:mmsi` and exposes
 * a tear-free snapshot of the latest position plus a connection-status
 * machine (`connecting | open | reconnecting | paused`).
 *
 * Why this hook on top of `useSSE`:
 *   - The live-track widget wants to push every new fix through React
 *     *and* let imperative MapLibre code mutate a marker on each frame.
 *     The hook owns the cross-cutting state (status, lastEventAt, latest
 *     position) so multiple consumers (overlay panel, bottom sheet, map
 *     marker) see the same snapshot without re-running the SSE listener.
 *   - The wire shape from `broadcastShipPosition` is the shared
 *     `ShipPosition` schema (mmsi/name/position/speedKnots/heading/...).
 *     This hook normalises `recordedAt` to a Date (SuperJSON sends it
 *     as `{ json, meta }` over our bridge — see safeRecordedAt).
 *
 * The hook does NOT fetch the initial trail — that belongs to
 * `useVesselTrail`. It is purely a real-time pipe.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ShipPosition } from "@hantavirus/shared";

import { useSSE, type SSEStatus } from "./useSSE.js";

/**
 * Wire-shape coming off `/sse/ships/:mmsi`. The producer in
 * `apps/api/src/sse-streams.ts` broadcasts a `ShipPosition` payload
 * verbatim, but SuperJSON over the bridge can turn `Date` into a
 * stringified-ISO; this type accepts either.
 */
export type ShipPositionFrame = Omit<ShipPosition, "recordedAt"> & {
  recordedAt: Date | string;
};

export interface UseShipPositionStreamOptions {
  /** When false, no connection is opened. */
  enabled?: boolean;
  /** Override the API base URL — used by tests. */
  apiBaseUrl?: string;
}

export interface UseShipPositionStreamResult {
  /** Last position received from the stream (or null until the snapshot lands). */
  latest: ShipPositionFrame | null;
  /** Wall-clock ms of the last frame (Date.now() at receive time). */
  lastEventAt: number | null;
  /** Cumulative count of frames received since mount. */
  frameCount: number;
  /** Fine-grained connection lifecycle for the status pill. */
  status: SSEStatus;
  /** Cumulative reconnects since mount; resets when the URL changes. */
  retryCount: number;
  /** Convenience flag — true when status === "reconnecting". */
  reconnecting: boolean;
  /** Convenience flag — true when status === "open". */
  connected: boolean;
  /** Manually trigger a reconnect immediately. */
  retry: () => void;
}

function buildStreamUrl(mmsi: string, apiBaseUrl?: string): string {
  const base =
    apiBaseUrl ??
    (typeof import.meta !== "undefined" && typeof import.meta.env !== "undefined"
      ? (import.meta.env.VITE_API_URL as string | undefined)
      : undefined) ??
    "";
  const safeBase = base.replace(/\/+$/u, "");
  return `${safeBase}/sse/ships/${encodeURIComponent(mmsi)}`;
}

function normaliseRecordedAt(value: Date | string | undefined): Date | string {
  if (!value) return new Date(0).toISOString();
  return value;
}

export function useShipPositionStream(
  mmsi: string | null | undefined,
  options: UseShipPositionStreamOptions = {},
): UseShipPositionStreamResult {
  const { enabled = true, apiBaseUrl } = options;

  const [latest, setLatest] = useState<ShipPositionFrame | null>(null);
  const [lastEventAt, setLastEventAt] = useState<number | null>(null);
  const [frameCount, setFrameCount] = useState(0);

  // Keep the latest setter behaviour in a ref so the handler identity
  // stays stable across renders — that prevents useSSE from tearing
  // down the connection on every parent render.
  const latestRef = useRef(latest);
  latestRef.current = latest;

  const handlePosition = useCallback((data: ShipPositionFrame) => {
    if (!data || !data.position) return;
    const { lat, lng } = data.position;
    if (typeof lat !== "number" || typeof lng !== "number") return;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return;
    setLatest({
      ...data,
      recordedAt: normaliseRecordedAt(data.recordedAt),
    });
    setLastEventAt(Date.now());
    setFrameCount((c) => c + 1);
  }, []);

  const url = mmsi && enabled ? buildStreamUrl(mmsi, apiBaseUrl) : "";

  const handlers = useMemo(
    () => ({ "ship.position": handlePosition }),
    [handlePosition],
  );

  const sse = useSSE<"ship.position", ShipPositionFrame>(url, handlers, {
    enabled: Boolean(mmsi && enabled),
  });

  // Reset the latest snapshot whenever the MMSI changes so a previous
  // vessel's position doesn't leak into the new subscription.
  useEffect(() => {
    setLatest(null);
    setLastEventAt(null);
    setFrameCount(0);
  }, [mmsi]);

  return {
    latest,
    lastEventAt,
    frameCount,
    status: sse.status,
    retryCount: sse.reconnectCount,
    reconnecting: sse.status === "reconnecting",
    connected: sse.status === "open",
    retry: sse.retry,
  };
}
